import { useEffect } from "react";

/**
 * useSeo — minimal head manager for static SEO landing pages.
 *
 * Sets <title>, <meta name="description">, <link rel="canonical">,
 * Open Graph tags, and injects one or more JSON-LD <script> tags
 * for the current page. All injected nodes are tagged with
 * data-seo-managed="true" so they get cleaned up on unmount,
 * preventing leaks across SPA route changes.
 *
 * Why a custom hook instead of react-helmet-async? Bundle size +
 * we only need ~30 LOC of behaviour. Googlebot fully renders the
 * SPA so dynamically-injected meta tags are honoured at index time.
 *
 * @param {object} cfg
 * @param {string} cfg.title          - <title> text
 * @param {string} cfg.description    - <meta name="description">
 * @param {string} cfg.canonical      - absolute URL on washpros.io
 * @param {string} [cfg.ogImage]      - absolute URL of social image
 * @param {Array<object>} [cfg.jsonLd] - array of JSON-LD objects to inject
 */
export const useSeo = ({ title, description, canonical, ogImage, ogType, twitterCard, robots, jsonLd = [] }) => {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousTitle = document.title;
    if (title) document.title = title;

    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => {
          if (k !== "content") el.setAttribute(k, v);
        });
        el.setAttribute("data-seo-managed", "true");
        document.head.appendChild(el);
      }
      el.setAttribute("content", attrs.content || "");
      return el;
    };

    const upsertLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute("data-seo-managed", "true");
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      return el;
    };

    if (description) {
      upsertMeta('meta[name="description"]', { name: "description", content: description });
      upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
      upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
      upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    }
    if (canonical) {
      upsertLink("canonical", canonical);
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    }
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
      upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    }
    if (ogType) {
      upsertMeta('meta[property="og:type"]', { property: "og:type", content: ogType });
    }
    if (twitterCard) {
      upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: twitterCard });
    }
    // `robots` lets a page opt out of indexing (e.g. admin-only
    // surfaces like /admin/brand-usage). Pass "noindex,nofollow,noarchive"
    // to mark a page as internal.
    if (robots) {
      upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    }

    // JSON-LD — append fresh nodes, remove on unmount
    const ldNodes = (Array.isArray(jsonLd) ? jsonLd : []).map((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-managed", "true");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = previousTitle;
      ldNodes.forEach((n) => {
        try { n.remove(); } catch (_e) { /* defensive */ }
      });
    };
  }, [title, description, canonical, ogImage, ogType, twitterCard, robots, JSON.stringify(jsonLd)]);
};

export default useSeo;
